import React from 'react';
import cx from 'classnames';

import { Input } from './Input';
import { ControlledTooltip, InputValidation } from '..';

const tooltipWidth = 350;

/**
 * An Input component that displays input validation errors in a tooltip positioned to the right of
 * the input field.
 * @param {ValidatedInputProps} props
 */
export const ValidatedInput = ({
    addClasses,
    autoComplete,
    disabled,
    expandLabel = true,
    handleBlur,
    handleChange,
    handleKeyPress,
    icon,
    inputId,
    label,
    labelClasses,
    padding,
    placeholder,
    showErrorsAs = 'tooltip',
    tooltipPosition = 'right',
    tabIndex,
    type,
    validationOutputs,
    value,
    width,
}: ValidatedInputProps): JSX.Element => {
    const firstValidationOutput = validationOutputs?.[0] ?? null;
    const validationOutput =
        firstValidationOutput?.inputId === inputId
            ? firstValidationOutput
            : null;

    if (showErrorsAs === 'text') {
        return (
            <div className="flex flex-col w-full items-end">
                {/* static heights prevent layout shift when errors are shown vs. hidden */}
                <div style={{ height: 21 }}>
                    {validationOutput !== null && (
                        <InputValidation
                            addClasses="text-highlight-magenta"
                            showOnlyFirstError={true}
                            validationOutputs={
                                validationOutput
                                    ? [validationOutput]
                                    : ([] as ValidationOutput[])
                            }
                        />
                    )}
                </div>
                <div style={{ height: 50 }}>
                    <Input
                        addClasses={addClasses}
                        autoComplete={autoComplete}
                        disabled={disabled}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        handleKeyPress={handleKeyPress}
                        icon={icon}
                        inputId={inputId}
                        label={label}
                        labelClasses={cx(labelClasses, {
                            'flex-grow': expandLabel,
                        })}
                        padding={padding}
                        placeholder={placeholder}
                        tabIndex={tabIndex}
                        type={type}
                        value={value}
                        width={width}
                    />
                </div>
            </div>
        );
    }

    const tooltip = (
        <ControlledTooltip
            position={tooltipPosition}
            message={
                <InputValidation
                    showOnlyFirstError={true}
                    validationOutputs={
                        validationOutput
                            ? [validationOutput]
                            : ([] as ValidationOutput[])
                    }
                />
            }
            minWidth={tooltipWidth}
            maxWidth={tooltipWidth}
        />
    );

    return (
        <div
            className={cx('flex w-full items-center', {
                'justify-start': tooltipPosition === 'right',
                'justify-end': tooltipPosition === 'left',
            })}
        >
            {validationOutput !== null && tooltipPosition !== 'right' && (
                <>{tooltip}</>
            )}
            <Input
                addClasses={addClasses}
                autoComplete={autoComplete}
                disabled={disabled}
                handleBlur={handleBlur}
                handleChange={handleChange}
                handleKeyPress={handleKeyPress}
                icon={icon}
                inputId={inputId}
                label={label}
                labelClasses={cx(labelClasses, {
                    'flex-grow': expandLabel,
                })}
                padding={padding}
                placeholder={placeholder}
                tabIndex={tabIndex}
                type={type}
                value={value}
                width={width}
            />
            {validationOutput !== null && tooltipPosition === 'right' && (
                <>{tooltip}</>
            )}
        </div>
    );
};
